import { scaleToDecimalPlaces } from "../../Generic/Numbers";
import { setOption, subscribeToOption } from "../Options/Options";
import { StringParser } from "../StringParser";
import { SerialPortHandler } from "./SerialPort";

class LineBreakTransformer implements Transformer<string, string> {
    protected chunks = "";

    public transform(
        chunk: string,
        controller: TransformStreamDefaultController<string>
    ): void {
        // Append to existing
        this.chunks += chunk;

        // Break by line break
        const lines = this.chunks.split(/\r\n|\r|\n/g);

        this.chunks = lines.pop() as string;

        for(let i = 0, l = lines.length; i < l; i++) {
            controller.enqueue(lines[i]);
        }
    }

    public flush(controller: TransformStreamDefaultController<string>): void {
        controller.enqueue(this.chunks);
    }
}

export type ScalesSettings = {
    id: string;
    enabled: true;
    port: SerialOptions;
    weightRequest: string;
    weightResponse: string;
    requestTimeout: number;
    decimalPlaceLength: number;
} | {
    enabled: false;
};

type ScaleResponseTemplateTypes = ["integer", "fractional", "wildcard"];
type ScaleResponseTemplatePart = {
    type: "literal";
    value: string;
} | {
    type: ScaleResponseTemplateTypes[number];
    value: number;
};

export class ScalesNotConnectedError extends Error {}
export class ScalesNotEnabledError extends Error {}
class InvalidValueFromScalesError extends Error {}

class Scales {
    protected port?: SerialPortHandler<string>;
    protected settings: ScalesSettings;
    protected isConnected: boolean;
    protected isEnabled: boolean;
    protected templateCache: Map<string, Array<ScaleResponseTemplatePart>>;
    protected responseTemplate: Array<ScaleResponseTemplatePart>;

    constructor() {
        this.isConnected = false;
        this.isEnabled = false;
        this.templateCache = new Map();
        this.loadSettings();
    }

    protected loadSettings(): void {
        const scaleOptions = subscribeToOption(
            "__scales",
            this.handleScaleSettingsUpdate
        );

        if(!scaleOptions) {
            this.settings = {
                enabled: false,
            };
        } else {
            this.settings = scaleOptions;
        }

        if(this.settings.enabled) {
            this.parseTemplate(this.settings.weightResponse);
            void this.connectToPort();
        }
    }

    protected handleScaleSettingsUpdate = (setting: "__scales", settings: ScalesSettings): void => {
        const wasPreviouslyEnabled = this.settings.enabled;

        this.settings = settings;

        if(this.settings.enabled) {
            this.parseTemplate(this.settings.weightResponse);
        }

        if(!wasPreviouslyEnabled && this.settings.enabled) {
            this.connectToPort();
        } else if(wasPreviouslyEnabled && !this.settings.enabled) {
            this.disconnectFromPort();
        }
    };

    protected updatePortId(id: string): void {
        if(!this.settings.enabled) {
            return;
        }

        setOption("__scales", {
            ...this.settings,
            id,
        });
    }

    protected async connectToPort(): Promise<void> {
        if(this.port) {
            await this.disconnectFromPort();
        }

        if(!this.settings.enabled) {
            return;
        }

        if(!this.isEnabled) {
            return;
        }

        this.port = new SerialPortHandler({
            port           : this.settings.port,
            transformReader: this.transformReader,
            id             : this.settings.id,
        });

        await this.port.open();

        const newPortId = this.port.getPortId();

        if(newPortId && newPortId !== this.settings.id) {
            this.updatePortId(newPortId);
        }

        this.isConnected = true;
    }

    protected async disconnectFromPort(): Promise<void> {
        if(!this.port) {
            return;
        }

        this.isConnected = false;

        await this.port.close();
        this.port = undefined;
    }

    protected transformReader(stream: ReadableStream<BufferSource>): {
        reader: ReadableStreamDefaultReader<string>;
        closePromise: Promise<void>;
    } {
        const decoder = new TextDecoderStream();
        const closePromise = stream.pipeTo(decoder.writable);
        const reader = decoder.readable
            .pipeThrough(new TransformStream(new LineBreakTransformer()))
            .getReader();

        return {
            reader,
            closePromise,
        };
    }

    protected parseTemplate(template: string): void {
        if(this.templateCache.has(template)) {
            this.responseTemplate = this.templateCache.get(template) as Array<ScaleResponseTemplatePart>;

            return;
        }

        const parts = StringParser.parseTemplate<ScaleResponseTemplateTypes>(template, [{
            value: "I",
            type : "integer",
        }, {
            value: "F",
            type : "fractional",
        }, {
            value: "?",
            type : "wildcard",
        }]);

        this.templateCache.set(template, parts);
        this.responseTemplate = parts;
    }

    protected getTemplatePartsFromValue(value: string): { integer: string; fractional: string; } {
        const parts = StringParser.fromParts(value, this.responseTemplate);
        let integerParts = "";
        let fractionParts = "";

        for(let i = 0, l = parts.length; i < l; i++) {
            switch(parts[i].type) {
                case "integer":
                    integerParts += parts[i].value;
                    break;
                case "fractional":
                    fractionParts += parts[i].value;
                    break;
            }
        }

        if(integerParts === "" && fractionParts === "") {
            throw new InvalidValueFromScalesError();
        }

        return {
            integer   : integerParts,
            fractional: fractionParts,
        };
    }

    protected isExpectedFormat = (data: string): boolean => {
        try {
            this.getTemplatePartsFromValue(data);

            return true;
        } catch {
            return false;
        }
    };

    protected formatWeight(weight: string): number {
        const parts = this.getTemplatePartsFromValue(weight);
        let int = parseInt(parts.integer);
        let frac = parseInt(parts.fractional);

        if(isNaN(int)) {
            int = 0;
        }

        if(isNaN(frac)) {
            frac = 0;
        }

        const parsedWeight = parseFloat(`${int}.${frac}`);

        if(!this.settings.enabled) {
            // Probably won't ever trigger, but in case it does.
            return parsedWeight;
        }

        return scaleToDecimalPlaces(parsedWeight, this.settings.decimalPlaceLength);
    }

    public async getWeight(): Promise<number> {
        if(!this.isEnabled || !this.settings.enabled) {
            throw new ScalesNotEnabledError();
        }

        if(!this.isConnected || !this.port) {
            throw new ScalesNotConnectedError();
        }

        try {
            const weight = await this.port.send(
                this.settings.weightRequest,
                this.isExpectedFormat,
                this.settings.requestTimeout
            );

            return this.formatWeight(weight);
        } catch(e) {
            return 0;
        }
    }

    public async disable(): Promise<void> {
        if(!this.isEnabled) {
            return;
        }

        this.isEnabled = false;
        await this.disconnectFromPort();
    }

    public async enable(): Promise<void> {
        if(this.isEnabled) {
            return;
        }

        this.isEnabled = true;
        try {
            await this.connectToPort();
        } catch(e) {
            console.error("Could not connect to the scales port", e);
            this.isEnabled = false;
        }
    }
}

export default new Scales();
